import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {MutateOptions} from '@tanstack/query-core';
import {
  OFFER_CATEGORIES,
  RESERVATION_PAYMENT_TYPES,
  PAYMENT_PROVIDERS,
  checkIsInternetConnectionFast,
  STATUSES,
  useStatus,
} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {useErrorModal} from '@chekin/shared';
import {
  useBuildTotalPayments,
  useFetchDeals,
  useMutateDeals,
  usePayment,
  usePaymentsCart,
  usePaymentSettings,
} from '../../hooks';
import {useSummary} from 'context/summary';
import {usePostHogTrack, POSTHOG_EVENTS} from '../../analytics';
import {useOnScreenChanged} from '../../hooks/embedded';
import {useBuildDisplayedPayments} from './useBuildDisplayedPayments';
import {checkIfTotalAmountToPayZero} from '../../utils/payments';
import {CustomIcon} from '@guestapp/old-ui-kit';
import PaymentItem from './PaymentItem';
import {EmptyState} from '../EmptyState/EmptyState';
import {PayLater} from './PayLater/PayLater';
import {
  ConfirmButton,
  Container,
  ContentCart,
  Divider,
  Hr,
  PaymentSummaryItemDescription,
  PaymentSummaryItemTitle,
  PaymentSummaryItemWrapper,
  StyledLoader,
  Summary,
  TotalPayment,
  Wrapper,
} from './styled';

type PaymentSummaryItemProps = {
  title: string;
  amount: string | number;
  description?: string;
};
function PaymentSummaryItem({title, description, amount}: PaymentSummaryItemProps) {
  return (
    <PaymentSummaryItemWrapper className="payment-summary__item">
      <PaymentSummaryItemTitle className="payment-summary__title">
        <p>{title}</p>
        <p>{amount}</p>
      </PaymentSummaryItemTitle>
      {description && (
        <PaymentSummaryItemDescription className="payment-summary__description">
          <CustomIcon name="information" size={20} />
          {/* <img width={20} height={20} src={info} alt="" /> */}
          <p>{description}</p>
        </PaymentSummaryItemDescription>
      )}
    </PaymentSummaryItemWrapper>
  );
}

function MyCart() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {displayError, ErrorModal} = useErrorModal();
  const {currencySymbol, currency} = usePaymentSettings();
  const {deleteDealMutation} = useMutateDeals({onError: displayError});
  const {trackEvent} = usePostHogTrack();

  const {getPayment} = useBuildDisplayedPayments();
  const {
    paymentsCart,
    isPaymentsCartLoading,
    isPaymentsCartFetching,
    isPaymentsCartEmpty,
    addDeferredPayments,
    removeDeferredPayment,
  } = usePaymentsCart({refetchOnWindowFocus: true});
  const isTotalAmountToPayZero = checkIfTotalAmountToPayZero(paymentsCart);
  const {totalToPayNow, totalDepositAmount} = useBuildTotalPayments({paymentsCart});
  const {paymentProvider} = usePaymentSettings();
  const {data: summary, reservationId} = useSummary();
  const {data: paginatedDealsList} = useFetchDeals({reservationId});
  const isBookingPaymentsActive = summary?.booking_payments?.active;

  const {isLoading: isPaymentProcessing, setStatus, resetStatus} = useStatus();

  const {pay: startPaymentProcess} = usePayment({
    onPaymentSuccess: () => {
      trackEvent(POSTHOG_EVENTS.PAYMENT_COMPLETED, {
        items_count: payNowPayments.length,
        item_ids: payNowPayments.map(p => p.id),
        total_amount: paymentsCart?.total_amount_to_pay,
      });
    },
    onPaymentExpired: resetStatus,
    onPaymentError: (_, errorMsg) => {
      trackEvent(POSTHOG_EVENTS.PAYMENT_ERROR, {
        error_message: errorMsg,
      });
      displayError(errorMsg);
      resetStatus();
    },
    onDataRequired: multiPayment => {
      navigate(PATHS.payments.form, {
        state: {
          payments: totalToPayNow,
          paymentId: multiPayment?.id,
          pay_later_payments: paymentsCart?.pay_later_payments,
          total_amount_to_pay: paymentsCart?.total_amount_to_pay,
        },
      });
    },
  });

  useOnScreenChanged({
    type: 'PAYMENTS_CART',
    enabled: !!paymentsCart,
    meta: {
      currency,
      total_amount_to_pay: paymentsCart?.total_amount_to_pay,
      pre_auth_payments_total: paymentsCart?.pre_auth_payments_total,
      retention_payments_total: paymentsCart?.retention_payments_total,
    },
  });

  const payNowPayments = React.useMemo(() => {
    return (paymentsCart?.pay_now_payments || []).map(getPayment);
  }, [getPayment, paymentsCart?.pay_now_payments]);

  const payLaterPayments = React.useMemo(() => {
    return (paymentsCart?.pay_later_payments || []).map(getPayment);
  }, [getPayment, paymentsCart?.pay_later_payments]);

  const handleRemove = React.useCallback(
    (id?: string, options?: MutateOptions<unknown, unknown, unknown>) => {
      if (id) {
        const removedPayment = payNowPayments.find(
          payment => payment.id === id || payment.externalId === id,
        );

        if (removedPayment?.objType === RESERVATION_PAYMENT_TYPES.upselling) {
          const externalId = removedPayment?.externalId?.replaceAll('-', '');
          const removedDeal = paginatedDealsList?.results.find(
            deal => deal.id === externalId,
          );

          if (removedDeal?.offer.category === OFFER_CATEGORIES.petFee) {
            toast.error('Pet Fees are not possible to be removed.');
            return false;
          }
        }

        deleteDealMutation(id, {
          ...options,
          onSuccess: (...args) => {
            trackEvent(POSTHOG_EVENTS.OFFER_REMOVED_FROM_CART, {
              offer_id: id,
              offer_title: removedPayment?.title,
            });
            options?.onSuccess?.(...args);
          },
        });
      }
    },
    [deleteDealMutation, paginatedDealsList?.results, payNowPayments, trackEvent],
  );

  const handlePayLater = React.useCallback(
    (id?: string) => {
      const addingPayment = paymentsCart?.pay_now_payments?.find(
        payment => payment.id === id,
      );

      if (addingPayment) {
        addDeferredPayments(addingPayment);
        trackEvent(POSTHOG_EVENTS.OFFER_ADDED_TO_PAY_LATER, {
          offer_id: id,
          offer_title: addingPayment.name,
          offer_price: addingPayment.amount,
        });
      }
    },
    [addDeferredPayments, paymentsCart?.pay_now_payments, trackEvent],
  );

  const handlePayNow = () => {
    trackEvent(POSTHOG_EVENTS.PAYMENT_INITIATED, {
      paymentProvider: paymentProvider,
      total_amount_to_pay: paymentsCart?.total_amount_to_pay || 0,
    });
    if (isTotalAmountToPayZero) return;

    if (paymentProvider === PAYMENT_PROVIDERS.addon && paymentsCart?.pay_now_payments) {
      setStatus(STATUSES.loading);
      startPaymentProcess({payments: paymentsCart?.pay_now_payments});
      return;
    }

    navigate(PATHS.payments.form, {
      state: {
        payments: totalToPayNow,
        pay_later_payments: paymentsCart?.pay_later_payments,
        total_amount_to_pay: paymentsCart?.total_amount_to_pay,
      },
    });
  };

  return (
    <Wrapper
      $isLoading={
        (!isPaymentsCartLoading && isPaymentsCartFetching) || isPaymentProcessing
      }
      className="my-cart"
    >
      <StyledLoader
        visible={isPaymentsCartLoading && !checkIsInternetConnectionFast()}
        width={80}
      />
      {isPaymentsCartEmpty && (
        <EmptyState
          imageSrc="emptyStateCart"
          title={t('the_cart_is_empty')}
          description={t('the_cart_is_empty_description')}
        />
      )}
      {
        <ContentCart $isInitialized={!!payNowPayments.length} className="my-cart__list">
          {payNowPayments.map(item => {
            return (
              <PaymentItem
                payment={item}
                key={item.id}
                onRemove={handleRemove}
                onPayLater={handlePayLater}
                currency={currencySymbol}
              />
            );
          })}
        </ContentCart>
      }
      {!!payNowPayments.length && <Divider />}
      {!isPaymentsCartEmpty && (
        <Container $isInitialized={!isPaymentsCartLoading}>
          <Summary className="payment-summary">
            <TotalPayment
              $isEmpty={isTotalAmountToPayZero}
              className="payment-summary__total"
            >
              <div>{t('to_pay_now')}</div>
              <div>
                {totalToPayNow || 0}
                {currencySymbol}
              </div>
            </TotalPayment>
            {!!parseFloat(paymentsCart?.retention_payments_total || '0') && (
              <PaymentSummaryItem
                title={t('retention')}
                amount={totalDepositAmount + currencySymbol}
                description={t('my_card_retention_explanation')}
              />
            )}
            {!!parseFloat(paymentsCart?.pre_auth_payments_total || '0') && (
              <PaymentSummaryItem
                title={t(`pre_authorizations`) + ':'}
                amount={
                  parseFloat(paymentsCart?.pre_auth_payments_total || '0').toFixed(2) +
                  currencySymbol
                }
                description={t('my_card_pre_auth_explanation')}
              />
            )}

            {Boolean(
              isBookingPaymentsActive &&
                !!parseFloat(paymentsCart?.outside_paid_amount || '0'),
            ) && (
              <>
                <Hr />
                <PaymentSummaryItem
                  title={t(`already_paid`) + ':'}
                  amount={paymentsCart?.outside_paid_amount + currencySymbol}
                />
              </>
            )}
          </Summary>

          <ConfirmButton
            loading={isPaymentProcessing}
            disabled={
              isPaymentsCartLoading ||
              isPaymentsCartEmpty ||
              isTotalAmountToPayZero ||
              isPaymentProcessing
            }
            onClick={handlePayNow}
          >
            {isPaymentProcessing ? t('Processing...') : t('confirm_transaction')}
          </ConfirmButton>
          {!!payLaterPayments.length && (
            <PayLater
              payments={payLaterPayments}
              currency={currencySymbol}
              onSendBackToCart={removeDeferredPayment}
            />
          )}
        </Container>
      )}
      <ErrorModal />
    </Wrapper>
  );
}

export {MyCart};
